import { useContext, useEffect, useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
	Button,
	Typography,
	TextField,
	CircularProgress,
	Box,
	Link,
	Snackbar,
	Alert,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import GetSupport from '../GetSupport';
import MFADialog from '../MFADialog';
import { useDispatch } from 'react-redux';
import { clearUser, setUser } from '../../services/features/authApi/authSlice';
import LoginPageLayout from '../LoginPageLayout';
import PasswordField from '../PasswordField';
import { GENERIC_ERROR } from '../../helper/constants';
import { useLoginMutation } from '../../services/features/authApi/authApi';
import { MfaType } from '../../shared/types/mfaTypes';

/**
 * Yup validation schema for the login form.
 */
export const validationSchema = Yup.object({
	email: Yup.string().email().required('Please enter your email'),
	password: Yup.string().min(4).max(25).required('Please enter your password'),
});

/**
 * Component for the login page.
 *
 * This component renders the login page user interface, allowing users to input their
 * email and password for authentication. It provides form validation, error handling,
 * and login functionality. Upon successful login, it navigates to the home page.
 *
 * @returns {JSX.Element} The rendered login page UI.
 */

const LoginPage = () => {
	const dispatch = useDispatch();

	const [login] = useLoginMutation();
	// const { login } = useContext(AuthContext);

	const [loading, setLoading] = useState(false);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const handleCloseSnackbar = () => {
		formik.setFieldValue('successMessage', '');
		setSnackbarOpen(false);
	};
	// const [genericError, setGenericError] = useState(false);
	// const [errorMessage, setErrorMessage] = useState('');

	// const [supportModalOpen, setSupportModalOpen] = useState(false);
	// const handleOpenSupportModal = () => setSupportModalOpen(true);
	// const handleCloseSupportModal = () => setSupportModalOpen(false);

	const [mfaType, setMfaType] = useState<MfaType>('setup');
	const [mfaDialogOpen, setMfaDialogOpen] = useState(false);
	const handleOpenMfaDialog = () => setMfaDialogOpen(true);
	const handleCloseMfaDialog = () => setMfaDialogOpen(false);

	const navigate = useNavigate();

	const handleLogin = async (username: string, password: string) => {
		return await login({ username, password })
			.unwrap()
			.then(async (userLogin) => {
				if (
					('requiresMFASetup' in userLogin && userLogin.requiresMFASetup) ||
					('requiresMFA' in userLogin && userLogin.requiresMFA) ||
					('requiresEmailMFA' in userLogin && userLogin.requiresEmailMFA)
				) {
					setMfaType(
						userLogin.requiresMFASetup
							? 'setup'
							: userLogin.requiresEmailMFA
							? 'email'
							: 'verify'
					);
					handleOpenMfaDialog();
				} else if ('id' in userLogin && 'token' in userLogin) {
					// dispatch(setUser(userLogin));
					return true;
				}
				return false;
			})
			.catch((e) => {
				console.log('Here is the login error', e);
				formik.setFieldValue(
					'errorMessage',
					(e?.status ?? 500) < 500
						? e.data
						: e?.data === 'Network Error'
						? 'Network error; please try again later'
						: GENERIC_ERROR
				);
				return false;
			});
	};

	// Formik form handling
	const formik = useFormik({
		initialValues: {
			email: '',
			password: '',
			errorMessage: '',
			successMessage: '',
		},
		validationSchema: validationSchema,
		onSubmit: async (values, { setFieldValue }) => {
			setFieldValue('errorMessage', '');
			setLoading(true);
			try {
				const res = await handleLogin(values.email, values.password);
				// console.log("RES", res);
				if (res === true) {
					// setGenericError(false);
					navigate('/home');
					// } else {
					// 	setGenericError(true);
					// 	if ((res?.status ?? 500) < 500) {
					// 		setErrorMessage(res.data);
					// 	} else {
					// 		setErrorMessage(GENERIC_ERROR);
					// 	}
				}
			} catch (error) {
				console.log('Error logging in', error);
				setFieldValue('errorMessage', GENERIC_ERROR);
				// setGenericError(true);
				// setErrorMessage(GENERIC_ERROR);
			} finally {
				setLoading(false);
			}
		},
	});

	useEffect(() => {
		if (formik.values.successMessage.length > 0 && !snackbarOpen) {
			setSnackbarOpen(true);
		}
	}, [formik.values.successMessage, snackbarOpen]);

	return (
		<>
			<LoginPageLayout
				cardTitle='Login'
				onSubmit={(e: any) => {
					e.preventDefault();
					formik.handleSubmit();
				}}
				HeaderComponent={
					<Link
						// component='button'
						variant='subtitle1'
						href='https://share.hsforms.com/1iAc6XnNWS8-IucPHebUE2ws5z79?utm_campaign=DeltaConnect_request_access_website&utm_source=DeltaConnect_website&utm_medium=website&utm_term=Delta%20Connect%2C%20quote%20and%20bind%2C%20Delta%20Insurance%20Australia'
						target='_blank'
						rel='noopener'
						// onClick={(e: any) => {
						// 	e.preventDefault();
						// 	handleOpenSupportModal();
						// }}
						sx={{
							textDecorationColor: '#474848',
							color: '#474848',
							textAlign: 'right',
						}}
					>
						Request Access
					</Link>
				}
				SubtitleComponent={
					<>
						<span style={{ marginRight: '0.5em' }}>
							{'By signing in you agree to our'}
						</span>
						<Link
							href='https://47308149.fs1.hubspotusercontent-na1.net/hubfs/47308149/AU/AU_documents_DIA%20Connect%20Broker%20Portal/Delta_Connect_Terms_January_2025_3eb37b7ff0.pdf'
							target='_blank'
							rel='noopener'
							color='inherit'
						>
							{'Terms and Conditions'}
						</Link>
						<span style={{ marginLeft: '0.5em' }}>{'of use'}</span>
					</>
				}
				FooterComponent={
					<Box display='flex' justifyContent='end'>
						<Link
							to={`/ResetPassword`}
							component={RouterLink}
							variant='subtitle1'
							sx={{
								textDecorationColor: '#474848',
								color: '#474848',
								textAlign: 'right',
							}}
						>
							Forgot Password?
						</Link>
					</Box>
				}
				FooterActions={
					<Button type='submit' variant='contained'>
						{loading ? <CircularProgress size={'2em'} color='inherit' /> : 'Login'}
					</Button>
				}
			>
				<TextField
					label='Email address'
					{...formik.getFieldProps('email')}
					fullWidth
					error={formik.touched.email && Boolean(formik.errors.email)}
					helperText={formik.touched.email && formik.errors.email}
				/>
				<PasswordField
					label='Password'
					// type='password'
					{...formik.getFieldProps('password')}
					fullWidth
					error={
						(formik.touched.password && Boolean(formik.errors.password)) ||
						formik.values.errorMessage.length > 0
					}
					helperText={
						formik.values.errorMessage.length > 0
							? formik.values.errorMessage
							: formik.touched.password && formik.errors.password
					}
				/>
			</LoginPageLayout>

			{/* {supportModalOpen && (
				<GetSupport open={supportModalOpen} handleClose={handleCloseSupportModal} />
			)} */}
			{mfaDialogOpen && (
				<MFADialog
					open={mfaDialogOpen}
					handleClose={(e: any) => handleCloseMfaDialog()}
					mfaType={mfaType}
					formik={formik}
				/>
			)}
			<Snackbar
				open={snackbarOpen}
				autoHideDuration={6000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert
					onClose={handleCloseSnackbar}
					severity='success'
					variant='filled'
					sx={{ width: '100%' }}
				>
					{formik.values.successMessage}
				</Alert>
			</Snackbar>
		</>
	);
};

export default LoginPage;
